/**
 * importJiraSemantic — the Jira runtime consumer.
 *
 * Structurally identical to importLinearSemantic (that's the point): read a
 * Jira project semantically, build the snapshot, hand it to the shared apply
 * path. Recovers issue links (blocks/relates/duplicates) and the epic/parent
 * hierarchy — the data the flat import drops. Identity was already registered
 * by the flat path; applySemanticImport re-registers idempotently.
 */

import { loadFieldMappingConfig, getColorRulesForEntity } from '../fieldMappingRegistry';
import { jiraImportSemantic } from './jiraSemanticApi';
import { buildJiraSnapshot } from './jiraSnapshot';
import { applySemanticImport, type SemanticImportResult } from './applySemanticImport';

export type JiraImportResult = SemanticImportResult;

export async function importJiraProjectSemantic(params: {
  workspaceId: string;
  projectKey: string;
  userId?: string;
  groupIntoCollection?: boolean;
}): Promise<{ data: JiraImportResult | null; error: string | null }> {
  const { workspaceId, projectKey, userId, groupIntoCollection = true } = params;

  let read;
  try {
    read = await jiraImportSemantic(workspaceId, projectKey);
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Jira read failed' };
  }

  const config = await loadFieldMappingConfig(workspaceId, 'jira');
  const snapshot = buildJiraSnapshot(read, {
    relationshipMap: config.relationshipMap,
    typeMap: config.typeMap,
    colorRules: getColorRulesForEntity(config.miroColorMaps ?? [], 'items'),
  });

  const externalUrlByExternalId = new Map(read.issues.map(i => [i.externalId, i.externalUrl]));
  const data = await applySemanticImport(snapshot, {
    workspaceId,
    connectorType: 'jira',
    sourceLabel: 'Jira import',
    sourceTag: 'jira-import',
    proposedByAgent: 'jira_import',
    containerTitle: read.project.name,
    externalUrlByExternalId,
    userId,
    groupIntoCollection,
  });
  return { data, error: null };
}
